/*
 * Utilities that deal with Box type objects.
 * Box objects are objects that have left, top, width and height.
 *
 * Example Box object:
 *
 * ```
 * const box = {
 *   left: 10,
 *   top: 20,
 *   width: 200,
 *   height: 100
 * }
 * ```
 */

import {clamp} from 'lodash';
import {either} from '@packages/utilities/fp/utils';

/*
Exact type was once intentionally removed  from Box annotation because anything that is a supertype of this shape is also allowed in here.
e.g. these BoxUtils should be typesafe with `BoxUtils.relative(getComputedStyle(element))`
or `BoxUtils.relative(element.getBoundingClientRect())`, but the exact shape means that these usages are disallowed.
*/

export type Box = {
  top: number;
  left: number;
  width: number;
  height: number;
  x?: number;
  y?: number;
};

type PositionDescriptor = {
  start: 'left' | 'top';
  end: 'right' | 'bottom';
  dimension: 'width' | 'height';
};

const x: PositionDescriptor = {
  start: 'left',
  end: 'right',
  dimension: 'width',
};

const y: PositionDescriptor = {
  start: 'top',
  end: 'bottom',
  dimension: 'height',
};

type SubBox = {
  top?: number;
  left?: number;
  width?: number;
  height?: number;
};

export const create = (
  box?: SubBox | Box | null
): {
  height: number;
  left: number;
  top: number;
  width: number;
} =>
  box
    ? {
        top: box.top || 0,
        left: box.left || 0,
        width: box.width || 0,
        height: box.height || 0,
      }
    : {
        top: 0,
        left: 0,
        width: 0,
        height: 0,
      };

export const equal = (box1: Box, box2: Box): boolean => {
  if (!box1 || !box2) {
    return false;
  }
  return (
    box1.left === box2.left &&
    box1.top === box2.top &&
    box1.width === box2.width &&
    box1.height === box2.height
  );
};

export const collides = (box1: Box, box2: Box): boolean => {
  if (!box1 || !box2) {
    return false;
  }
  return (
    // Left edge of first box is to the left of the second box's right edge:
    box1.left < box2.left + box2.width &&
    // Right edge of the first box is to the right of the second box's left edge:
    box1.left + box1.width > box2.left &&
    // Top edge of the first box is above the second box's bottom edge:
    box1.top < box2.top + box2.height && // Bottom edge of the first box is below the second box's top edge:
    box1.top + box1.height > box2.top
  );
};

/**
 * Returns whether one box (box2) is fully contained within the other (box1).
 */
export const within = (box1: Box, box2: Box): boolean => {
  if (!box1 || !box2) {
    return false;
  }
  return (
    // Left edge of first box is to the left of the second box's left edge
    box1.left <= box2.left &&
    // Right edge of the first box is to the right of the second box's right edge
    box1.left + box1.width >= box2.left + box2.width &&
    // Top edge of the first box is above the second box's top edge
    box1.top <= box2.top &&
    // Bottom edge of the first box is below the second box's bottom edge
    box1.top + box1.width >= box2.top + box2.height
  );
};

export const pad =
  (amount: number): ((arg1: Box) => Box) =>
  ({top, left, width, height}: Box): Box => ({
    top: top - amount,
    left: left - amount,
    width: width + 2 * amount,
    height: height + 2 * amount,
  });

export const padHorizontal =
  (amount: number): ((arg1: Box) => Box) =>
  ({top, left, width, height}: Box): Box => ({
    top,
    left: left - amount,
    width: width + 2 * amount,
    height,
  });

export const relative =
  (viewport: Box): ((arg1: Box) => Box) =>
  ({top, left, width, height}: Box): Box => ({
    top: top - viewport.top,
    left: left - viewport.left,
    width,
    height,
  });

export const contain =
  (viewport: Box): ((arg1: Box) => Box) =>
  ({top, left, width, height}: Box): Box => ({
    top: clamp(top, viewport.top, viewport.top + viewport.height - height),
    left: clamp(left, viewport.left, viewport.left + viewport.width - width),
    width,
    height,
  });

type PositionType = (
  arg1: PositionDescriptor,
  arg2: PositionDescriptor,
  arg3?: boolean | null | undefined
) => (viewport: Box, anchor: Box, popover: Box) => Box;

const position: PositionType =
  (main, {start, dimension}, flip) =>
  (viewport: Box, anchor: Box, popover: Box) => {
    const topOrLeft = clamp(
      anchor[main.start] +
        (flip ? anchor[main.dimension] : -popover[main.dimension]),
      viewport[main.start],
      viewport[main.start] + viewport[main.dimension] - popover[main.dimension]
    );

    const leftOrTop = clamp(
      anchor[start] + anchor[dimension] / 2 - popover[dimension] / 2,
      viewport[start],
      viewport[start] + viewport[dimension] - popover[dimension]
    );

    const {width, height} = popover;

    return main.start === 'top'
      ? {top: topOrLeft, left: leftOrTop, width, height}
      : {top: leftOrTop, left: topOrLeft, width, height};
  };

const over = (anchor: Box, popover: Box): Box => ({
  top: Math.ceil(anchor.top + (anchor.height - popover.height) / 2),
  left: Math.ceil(anchor.left + (anchor.width - popover.width) / 2),
  width: popover.width,
  height: popover.height,
});

/**
 * Place `popover` over `anchor` but then move it so that is stays contained
 * within the viewport when possible.
 */
const centerAnchorBox = (viewport: Box, anchor: Box, popover: Box): Box =>
  contain(viewport)(over(anchor, popover));

const centerOffsetForAxis =
  ({start, dimension}: PositionDescriptor) =>
  (anchor: Box, popover: Box) =>
    anchor[start] + anchor[dimension] / 2 - popover[start];

const startSide =
  ({start, dimension}: PositionDescriptor) =>
  (viewport: any, anchor: any, popover: any) =>
    anchor[start] - popover[dimension] >= viewport[start] && start;

const endSide =
  ({start, end, dimension}: PositionDescriptor) =>
  (viewport: any, anchor: any, popover: any) =>
    anchor[start] + anchor[dimension] + popover[dimension] <=
      viewport[start] + viewport[dimension] && end;

const sideTop = startSide(y);
const sideBottom = endSide(y);
const sideLeft = startSide(x);
const sideRight = endSide(x);
const fitsTopOrBottom = either(sideTop, sideBottom);
const fitsBottomOrTop = either(sideBottom, sideTop);
const fitsLeftOrRight = either(sideLeft, sideRight);
const fitsRightOrLeft = either(sideRight, sideLeft);

export const positions = {
  top: position(y, x) as (viewport: Box, anchor: Box, popover: Box) => Box,
  bottom: position(y, x, true) as (
    viewport: Box,
    anchor: Box,
    popover: Box
  ) => Box,
  left: position(x, y) as (viewport: Box, anchor: Box, popover: Box) => Box,
  right: position(x, y, true) as (
    viewport: Box,
    anchor: Box,
    popover: Box
  ) => Box,
  center: centerAnchorBox,
} as const;

const alwaysCenter = (_viewport: Box, _anchor: Box, _popover: Box): string =>
  'center';

export const placements = {
  top: either(either(fitsTopOrBottom, fitsLeftOrRight), alwaysCenter) as (
    ...args: Array<any>
  ) => any,
  bottom: either(either(fitsBottomOrTop, fitsRightOrLeft), alwaysCenter) as (
    ...args: Array<any>
  ) => any,
  left: either(either(fitsLeftOrRight, fitsTopOrBottom), alwaysCenter) as (
    ...args: Array<any>
  ) => any,
  right: either(either(fitsRightOrLeft, fitsBottomOrTop), alwaysCenter) as (
    ...args: Array<any>
  ) => any,
  center: alwaysCenter,
} as const;

export type PlacementIdentifier = keyof typeof placements;

export const place =
  (
    placement: PlacementIdentifier
  ): ((viewport: Box, anchor: Box, popover: Box) => PlacementIdentifier) =>
  (viewport: Box, anchor: Box, popover: Box): PlacementIdentifier =>
    placements[placement](viewport, anchor, popover);

export const centerOffset = {
  top: centerOffsetForAxis(x) as (anchor: Box, popover: Box) => number,
  bottom: centerOffsetForAxis(x) as (anchor: Box, popover: Box) => number,
  left: centerOffsetForAxis(y) as (anchor: Box, popover: Box) => number,
  right: centerOffsetForAxis(y) as (anchor: Box, popover: Box) => number,
  center: (_a: Box, _b: Box): number => 0,
} as const;

export const scaleToFit =
  (target: Box): ((box: Box) => Box) =>
  (box: Box): Box =>
    box.width >= box.height && box.width > target.width
      ? {
          top: box.top,
          left: box.left,
          width: target.width,
          height: (box.height * target.width) / box.width,
        }
      : box.height > box.width && box.height > target.height
        ? {
            top: box.top,
            left: box.left,
            width: (box.width * target.height) / box.height,
            height: target.height,
          }
        : box;
